import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import supabase from "../supabaseClient";
import {
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  IconButton,
  Paper,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";
import {
  Delete,
  ArrowUpward,
  ArrowDownward,
  AutoAwesome,
  // Edit as EditIcon,
} from "@mui/icons-material";
import isEqual from "lodash.isequal"; // Import isEqual function
import UserContext from "../contexts/userContext";


function RecipeEditor() {
  const {user} = useContext(UserContext)
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [initialRecipe, setInitialRecipe] = useState(null);
  const [loading, setLoading] = useState(true);

  // Form fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [ingredients, setIngredients] = useState([]);
  const [instructions, setInstructions] = useState([]);
  const [imageUrl, setImageUrl] = useState("");
  const [newImageFile, setNewImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  // Loading state for image upload
  const [imageUploading, setImageUploading] = useState(false);
  
  // Loading state for AI image generation
  const [generatingAIImage, setGeneratingAIImage] = useState(false);
  
  // Streaming image generation state
  const [partialImageData, setPartialImageData] = useState(null);
  const [blurReveal, setBlurReveal] = useState(0); // Percentage of image revealed (blur overlay moves down)
  const blurAnimationRef = useRef(null);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Track if changes have been made
  const [hasChanges, setHasChanges] = useState(false);

  // Validation errors
  const [ingredientErrors, setIngredientErrors] = useState([]);
  const [instructionErrors, setInstructionErrors] = useState([]);

  // Form submission state
  const [formSubmitted, setFormSubmitted] = useState(false);

  // State to track new additions
  const [newIngredientAdded, setNewIngredientAdded] = useState(false);
  const [newInstructionAdded, setNewInstructionAdded] = useState(false);

  // Refs for ingredients and instructions
  const ingredientNameRefs = useRef([]);
  const instructionRefs = useRef([]);

  const [authorized, setAuthorized] = useState(false); // Authorization state

  console.log(user)


  useEffect(() => {
    // Fetch the recipe from Supabase
    const fetchRecipe = async () => {
      const { data, error } = await supabase
        .from("recipes")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error fetching recipe:", error);
        return;
      }

            // Check if the user is either the recipe owner or an admin
            if (user && (user.id === data.user_id || user.user_role === "admin")) {
              setAuthorized(true); // User is authorized
            } else {
              setAuthorized(false); // User is not authorized
            }
      

      setInitialRecipe(data);

      setTitle(data.title || "");
      setDescription(data.description || "");

      // Initialize ingredients with isDeleted flag, touched state, and isNew
      const ingredientsWithFlags = (data.ingredients || []).map(
        (ingredient) => ({
          ...ingredient,
          isDeleted: false,
          touched: false,
          isNew: false,
        })
      );
      setIngredients(ingredientsWithFlags);

      // Initialize instructions with isDeleted flag, touched state, and isNew
      const instructionsWithFlags = (
        Array.isArray(data.instructions) ? data.instructions : []
      ).map((instruction) => ({
        text: instruction,
        isDeleted: false,
        touched: false,
        isNew: false,
      }));
      setInstructions(instructionsWithFlags);

      setImageUrl(data.image_url || "");
      setLoading(false);
    };

    fetchRecipe();
  }, [id, user]);

  // Helper functions to check if item is empty
  const isIngredientEmpty = (ingredient) => {
    return (
      (!ingredient.name || ingredient.name.trim() === "") &&
      (!ingredient.quantity.value ||
        ingredient.quantity.value.toString().trim() === "") &&
      (!ingredient.quantity.unit ||
        ingredient.quantity.unit.toString().trim() === "") &&
      (!ingredient.preparation || ingredient.preparation.trim() === "")
    );
  };

  const isInstructionEmpty = (instruction) => {
    return !instruction.text || instruction.text.trim() === "";
  };

  // Validation functions
  const validateIngredients = useCallback(() => {
    const errors = ingredients.map((ingredient) => {
      if (ingredient.isDeleted) return null; // Skip deleted ingredients
      const error = {};
      const hasName = ingredient.name && ingredient.name.trim() !== "";
      const hasQuantity =
        ingredient.quantity &&
        ingredient.quantity.value != null &&
        ingredient.quantity.value.toString().trim() !== "";

      if (!hasName) error.name = "required";
      if (!hasQuantity) error.quantity = "required";

      return Object.keys(error).length > 0 ? error : null;
    });
    setIngredientErrors(errors);
    return errors.every((error) => error === null);
  }, [ingredients]);

  const validateInstructions = useCallback(() => {
    const errors = instructions.map((instruction) => {
      if (instruction.isDeleted) return null;
      const error = {};
      const hasText = instruction.text && instruction.text.trim() !== "";
      if (!hasText) error.text = "instruction required";
      return Object.keys(error).length > 0 ? error : null;
    });
    setInstructionErrors(errors);
    return errors.every((error) => error === null);
  }, [instructions]);

  // Function to collect current form data into a single object
  const getCurrentFormData = useCallback(() => {
    return {
      title,
      description,
      ingredients: ingredients
        .filter((ing) => !ing.isDeleted)
        .map(({ isDeleted, id, isNew, touched, ...rest }) => rest),
      instructions: instructions
        .filter((instr) => !instr.isDeleted)
        .map(({ text }) => text), // Removed .sort() to preserve order
      image_url: newImageFile ? "new_image" : imageUrl,
    };
  }, [title, description, ingredients, instructions, imageUrl, newImageFile]);

  // Validation and change detection
  useEffect(() => {
    if (initialRecipe) {
      const currentData = getCurrentFormData();

      const initialData = {
        title: initialRecipe.title || "",
        description: initialRecipe.description || "",
        ingredients: (initialRecipe.ingredients || []).map(
          ({ id, ...rest }) => rest
        ),
        instructions: Array.isArray(initialRecipe.instructions)
          ? initialRecipe.instructions
          : [], // Removed .sort()
        image_url: initialRecipe.image_url || "",
      };

      // Compare current data with initial data
      const dataChanged = !isEqual(currentData, initialData);

      // Validate inputs
      const ingredientsValid = validateIngredients();
      const instructionsValid = validateInstructions();

      setHasChanges(dataChanged && ingredientsValid && instructionsValid);
    }
  }, [
    title,
    description,
    ingredients,
    instructions,
    imageUrl,
    initialRecipe,
    newImageFile,
    getCurrentFormData,
    validateIngredients,
    validateInstructions,
  ]);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewImageFile(file);
      // Create a preview URL to display the new image before uploading
      setImagePreviewUrl(URL.createObjectURL(file));
    }
  };

  // Animate blur reveal from current position to target
  const animateBlurReveal = useCallback((targetPercent) => {
    if (blurAnimationRef.current) {
      cancelAnimationFrame(blurAnimationRef.current);
    }
    
    const duration = 7000; // 7 seconds - slow, smooth reveal
    const startTime = performance.now();
    let startPercent = 0;
    
    // Get current value and start animation
    setBlurReveal(prev => {
      startPercent = prev;
      
      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Ease out for smooth deceleration
        const eased = 1 - Math.pow(1 - progress, 2);
        const newPercent = startPercent + (targetPercent - startPercent) * eased;
        
        setBlurReveal(newPercent);
        
        if (progress < 1) {
          blurAnimationRef.current = requestAnimationFrame(animate);
        }
      };
      
      blurAnimationRef.current = requestAnimationFrame(animate);
      return prev; // Don't change state in this setter
    });
  }, []);

  // Generate AI image for the recipe with streaming support
  const handleGenerateAIImage = async () => {
    setGeneratingAIImage(true);
    setPartialImageData(null);
    setBlurReveal(0); // Reset blur reveal
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const apiUrl = `${process.env.REACT_APP_SERVER_URL || ''}/api/recipes/${id}/generate-image/stream`;
      console.log('[SSE] Starting streaming request to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
          'Accept': 'text/event-stream',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[SSE] HTTP error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('[SSE] Response received, starting to read stream...');
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let resultUrl = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        
        // SSE events are delimited by double newlines (\n\n)
        // Process only complete events
        let eventEnd;
        while ((eventEnd = buffer.indexOf('\n\n')) !== -1) {
          const eventBlock = buffer.slice(0, eventEnd);
          buffer = buffer.slice(eventEnd + 2);
          
          // Skip empty blocks and keep-alive pings
          if (!eventBlock.trim() || eventBlock.trim() === ':keep-alive') continue;
          
          // Parse the event block
          let eventType = null;
          let eventData = null;
          
          const lines = eventBlock.split('\n');
          for (const line of lines) {
            if (line.startsWith(':')) continue; // Comment/ping
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6);
            }
          }
          
          if (!eventType || !eventData) continue;
          
          console.log('[SSE] Complete event:', eventType, eventData.length > 100 ? `(${eventData.length} chars)` : eventData);
          
          try {
            const parsed = JSON.parse(eventData);
            
            switch (eventType) {
              case 'status':
                // Status updates logged for debugging
                console.log('[SSE] Status:', parsed.message || parsed.status);
                break;
              
              case 'partial_image':
                console.log('[SSE] 🖼️ Partial image', parsed.partial_index, '/', parsed.total_partials);
                setPartialImageData(`data:image/png;base64,${parsed.b64_json}`);
                // Server sends 1-based indices: 1, 2, 3 for 3 partials
                // First partial (index 1) just sets dimensions/colors - keep full blur, NO animation
                // Start revealing from second partial (index 2) onward
                if (parsed.partial_index <= 1) {
                  // Cancel any animation and force full blur
                  if (blurAnimationRef.current) {
                    cancelAnimationFrame(blurAnimationRef.current);
                    blurAnimationRef.current = null;
                  }
                  setBlurReveal(0);
                } else {
                  // OpenAI's render progress isn't linear - early partials render less
                  // Be conservative to always stay "behind" where OpenAI actually renders
                  // With 3 partials (1-based: 1,2,3):
                  // index 2 → ~20% revealed (OpenAI renders ~25-30%)
                  // index 3 → ~50% revealed (OpenAI renders ~75-80%)
                  // completed → 100%
                  const revealTargets = {
                    2: 20,  // First real partial - conservative
                    3: 50,  // Second real partial - still conservative
                  };
                  const targetReveal = revealTargets[parsed.partial_index] || 50;
                  animateBlurReveal(targetReveal);
                }
                break;
              
              case 'completed':
                console.log('[SSE] ✅ Completed with URL:', parsed.image_url);
                // Cancel animation and snap to fully revealed
                if (blurAnimationRef.current) {
                  cancelAnimationFrame(blurAnimationRef.current);
                }
                setBlurReveal(100);
                resultUrl = parsed.image_url;
                setImageUrl(parsed.image_url);
                setNewImageFile(null);
                setImagePreviewUrl("");
                setPartialImageData(null);
                setInitialRecipe((prev) => ({ ...prev, image_url: parsed.image_url }));
                setSnackbarOpen(true);
                break;
              
              case 'error':
                throw new Error(parsed.error);
              
              default:
                break;
            }
          } catch (parseError) {
            console.error('[SSE] Parse error:', parseError.message);
          }
        }
      }
      
      return resultUrl;
    } catch (error) {
      console.error('Error generating AI image:', error);
      alert(`Failed to generate AI image: ${error.message}`);
    } finally {
      setGeneratingAIImage(false);
      setPartialImageData(null);
      if (blurAnimationRef.current) {
        cancelAnimationFrame(blurAnimationRef.current);
      }
    }
  };

  // Cleanup blur animation on unmount
  useEffect(() => {
    return () => {
      if (blurAnimationRef.current) {
        cancelAnimationFrame(blurAnimationRef.current);
      }
    };
  }, []);

  // Check if image is AI-generated based on URL pattern
  const isAIGeneratedImage = (url) => {
    return url && (url.includes('/ai-') || url.includes('recipe-images/ai-'));
  };

  const handleSave = async () => {
    setFormSubmitted(true);

    const ingredientsValid = validateIngredients();
    const instructionsValid = validateInstructions();

    if (!ingredientsValid || !instructionsValid) {
      // Validation failed, do not proceed with save
      return;
    }

    setLoading(true);
    let updatedImageUrl = imageUrl;

    if (newImageFile) {
      setImageUploading(true);

      // Upload the new image to Supabase Storage
      const fileExt = newImageFile.name.split(".").pop();
      const fileName = `${id}-${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      // Upload to 'recipe-images' bucket
      const { error: uploadError } = await supabase.storage
        .from("recipe-images")
        .upload(filePath, newImageFile);

      if (uploadError) {
        console.error("Error uploading image:", uploadError);
        setLoading(false);
        setImageUploading(false);
        return;
      }

      // Get the public URL for the image
      const { data: publicURLData } = supabase.storage
        .from("recipe-images")
        .getPublicUrl(filePath);

      updatedImageUrl = publicURLData.publicUrl;
      setImageUploading(false);
    }

    // Prepare data for saving
    const ingredientsToSave = ingredients
      .filter((ingredient) => !ingredient.isDeleted)
      .map(({ isDeleted, isNew, touched, ...rest }) => rest);

    const instructionsToSave = instructions
      .filter((instruction) => !instruction.isDeleted)
      .map((instruction) => instruction.text);

    // Update the recipe in Supabase
    const { error } = await supabase
      .from("recipes")
      .update({
        title,
        description,
        ingredients: ingredientsToSave,
        instructions: instructionsToSave,
        image_url: updatedImageUrl,
      })
      .eq("id", id);

    if (error) {
      console.error("Error updating recipe:", error);
    } else {
      // Show success notification
      setSnackbarOpen(true);

      // Update ingredients and instructions state to reflect deleted items and mark as existing
      setIngredients(
        ingredientsToSave.map((ingredient) => ({
          ...ingredient,
          isDeleted: false,
          touched: false,
          isNew: false,
        }))
      );

      setInstructions(
        instructionsToSave.map((text) => ({
          text,
          isDeleted: false,
          touched: false,
          isNew: false,
        }))
      );

      // After saving, update initialRecipe to the current data
      setInitialRecipe({
        title,
        description,
        ingredients: ingredientsToSave,
        instructions: instructionsToSave,
        image_url: updatedImageUrl,
      });

      // Reset form submission state
      setFormSubmitted(false);

      // Reset hasChanges to false
      setHasChanges(false);

      // Reset newImageFile if any
      if (newImageFile) {
        setNewImageFile(null);
        setImagePreviewUrl("");
      }
    }
    setLoading(false);
    navigate(-1)
  };

  // Ingredient handling functions
  const addIngredient = () => {
    setIngredients((prevIngredients) => {
      const newIngredient = {
        id: uuidv4(),
        name: "",
        quantity: { value: "", unit: "" },
        preparation: "",
        isDeleted: false,
        touched: false,
        isNew: true,
      };
      const updatedIngredients = [...prevIngredients, newIngredient];
      setNewIngredientAdded(true); // Set flag to focus the new ingredient
      return updatedIngredients;
    });
    setIngredientErrors((prevErrors) => [...prevErrors, null]); // Add placeholder for error
  };

  const updateIngredient = (index, field, value) => {
    const newIngredients = [...ingredients];
    newIngredients[index][field] = value;
    setIngredients(newIngredients);
  };

  const updateIngredientQuantity = (index, field, value) => {
    const newIngredients = [...ingredients];
    newIngredients[index].quantity = {
      ...newIngredients[index].quantity,
      [field]: value,
    };
    setIngredients(newIngredients);
  };

  const handleIngredientBlur = (index) => {
    const newIngredients = [...ingredients];
    newIngredients[index].touched = true;
    setIngredients(newIngredients);
    validateIngredients(); // Update errors immediately on blur
  };

  const deleteIngredient = (index) => {
    const ingredient = ingredients[index];

    if (ingredient.isNew && isIngredientEmpty(ingredient)) {
      // Remove the ingredient immediately if it's new and empty
      const newIngredients = ingredients.filter((_, i) => i !== index);
      setIngredients(newIngredients);
      // Also remove the corresponding error entry
      const newErrors = ingredientErrors.filter((_, i) => i !== index);
      setIngredientErrors(newErrors);
    } else {
      // Otherwise, soft delete
      const newIngredients = [...ingredients];
      newIngredients[index].isDeleted = true;
      setIngredients(newIngredients);
    }
  };

  const restoreIngredient = (index) => {
    const newIngredients = [...ingredients];
    newIngredients[index].isDeleted = false;
    setIngredients(newIngredients);
  };

  // Instruction handling functions
  const addInstruction = () => {
    setInstructions((prevInstructions) => {
      const newInstruction = {
        text: "",
        isDeleted: false,
        touched: false,
        isNew: true,
      };
      const updatedInstructions = [...prevInstructions, newInstruction];
      setNewInstructionAdded(true); // Set flag to focus the new instruction
      return updatedInstructions;
    });
    setInstructionErrors((prevErrors) => [...prevErrors, null]); // Add placeholder for error
  };

  const updateInstruction = (index, value) => {
    const newInstructions = [...instructions];
    newInstructions[index].text = value;
    setInstructions(newInstructions);
  };

  const handleInstructionBlur = (index) => {
    const newInstructions = [...instructions];
    newInstructions[index].touched = true;
    setInstructions(newInstructions);
    validateInstructions(); // Update errors immediately on blur
  };

  const deleteInstruction = (index) => {
    const instruction = instructions[index];

    if (instruction.isNew && isInstructionEmpty(instruction)) {
      // Remove the instruction immediately if it's new and empty
      const newInstructions = instructions.filter((_, i) => i !== index);
      setInstructions(newInstructions);
      // Also remove the corresponding error entry
      const newErrors = instructionErrors.filter((_, i) => i !== index);
      setInstructionErrors(newErrors);
    } else {
      // Otherwise, soft delete
      const newInstructions = [...instructions];
      newInstructions[index].isDeleted = true;
      setInstructions(newInstructions);
    }
  };

  const restoreInstruction = (index) => {
    const newInstructions = [...instructions];
    newInstructions[index].isDeleted = false;
    setInstructions(newInstructions);
  };

  const moveInstructionUp = (index) => {
    if (index === 0) return; // Can't move up the first instruction
    const newInstructions = [...instructions];
    [newInstructions[index - 1], newInstructions[index]] = [
      newInstructions[index],
      newInstructions[index - 1],
    ];
    setInstructions(newInstructions);
  };

  const moveInstructionDown = (index) => {
    if (index === instructions.length - 1) return; // Can't move down the last instruction
    const newInstructions = [...instructions];
    [newInstructions[index + 1], newInstructions[index]] = [
      newInstructions[index],
      newInstructions[index + 1],
    ];
    setInstructions(newInstructions);
  };

  // Focus on newly added ingredient
  useEffect(() => {
    if (newIngredientAdded) {
      const lastIndex = ingredients.length - 1;
      const inputElement = ingredientNameRefs.current[lastIndex];
      if (inputElement) {
        inputElement.focus();
      }
      setNewIngredientAdded(false);
    }
  }, [ingredients, newIngredientAdded]);

  // Focus on newly added instruction
  useEffect(() => {
    if (newInstructionAdded) {
      const lastIndex = instructions.length - 1;
      const inputElement = instructionRefs.current[lastIndex];
      if (inputElement) {
        inputElement.focus();
      }
      setNewInstructionAdded(false);
    }
  }, [instructions, newInstructionAdded]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!authorized) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <Typography variant="h5">
          Unauthorized: You don't have permission to edit this recipe.
        </Typography>
      </Box>
    );
  }


  return (
    <Box sx={{ padding: 2, maxWidth: "800px", margin: "0 auto" }}>
      <Typography
        variant="h5"
        sx={{
          fontSize: "1.3rem",
          fontFamily: theme.typography.fontFamily,
          cursor: "pointer",
          marginBottom: ".5em",
        }}
      >
        edit
      </Typography>
      <TextField
        label="title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        fullWidth
        margin="normal"
      />
      <TextField
        label="description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        margin="normal"
        multiline
        rows={3}
      />

      {/* Image Upload Section */}
      <Typography
        variant="h6"
        gutterBottom
        sx={{ marginTop: 3, marginBottom: 2 }}
      >
        image
      </Typography>
      <Box
        sx={{
          position: "relative",
          marginBottom: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Loading overlay for uploading */}
        {imageUploading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 1,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255,255,255,0.85)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {/* Show shimmer placeholder when generating and no partial yet */}
        {generatingAIImage && !partialImageData ? (
          <Box
            sx={{
              width: "100%",
              maxWidth: "400px",
              aspectRatio: "1",
              borderRadius: "8px",
              background: theme.palette.mode === 'dark' 
                ? 'linear-gradient(110deg, #1a1a1a 8%, #2d2d2d 18%, #1a1a1a 33%)'
                : 'linear-gradient(110deg, #e8e8e8 8%, #f5f5f5 18%, #e8e8e8 33%)',
              backgroundSize: '200% 100%',
              animation: 'shimmerPlaceholder 1.5s linear infinite',
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              "@keyframes shimmerPlaceholder": {
                "0%": { backgroundPosition: "200% 0" },
                "100%": { backgroundPosition: "-200% 0" },
              },
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                border: "3px solid",
                borderColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.1)' 
                  : 'rgba(0,0,0,0.1)',
                borderTopColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.6)'
                  : 'rgba(0,0,0,0.4)',
                animation: 'spin 1s linear infinite',
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            />
          </Box>
        ) : partialImageData ? (
          /* Partial image with blur overlay that wipes down */
          <Box sx={{ position: "relative", width: "100%", maxWidth: "400px", overflow: "hidden", borderRadius: "8px" }}>
            {/* Sharp image layer */}
            <img
              src={partialImageData}
              alt="Recipe generating"
              style={{
                width: "100%",
                borderRadius: "8px",
                objectFit: "cover",
                maxHeight: "300px",
                display: "block",
              }}
            />
            {/* Blur overlay that clips from blurReveal% down to 100% */}
            {blurReveal < 100 && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  clipPath: `inset(${blurReveal}% 0 0 0)`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <img
                  src={partialImageData}
                  alt=""
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    objectFit: "cover",
                    maxHeight: "300px",
                    filter: "blur(20px) brightness(1.05)",
                    transform: "scale(1.1)", // Prevent blur edge artifacts
                  }}
                />
                {/* Shimmer overlay to show activity */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 40%, rgba(255,255,255,0.25) 50%, rgba(255,255,255,0.18) 60%, transparent 100%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmerOverlay 2.5s ease-in-out infinite",
                    "@keyframes shimmerOverlay": {
                      "0%": { backgroundPosition: "200% 0" },
                      "100%": { backgroundPosition: "-200% 0" },
                    },
                  }}
                />
              </Box>
            )}
            {/* Progress bar at bottom */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: 3,
                borderRadius: "0 0 8px 8px",
                overflow: "hidden",
                backgroundColor: "rgba(0,0,0,0.2)",
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${blurReveal}%`,
                  background: "linear-gradient(90deg, #7c3aed, #a855f7)",
                }}
              />
            </Box>
          </Box>
        ) : (imageUrl || imagePreviewUrl) && !generatingAIImage ? (
          /* Final image display (not generating) */
          <Box sx={{ position: "relative", width: "100%", maxWidth: "400px" }}>
            <img
              src={imagePreviewUrl || imageUrl}
              alt="Recipe"
              style={{
                width: "100%",
                borderRadius: "8px",
                objectFit: "cover",
                maxHeight: "300px",
              }}
            />
            {/* AI Generated Badge */}
            {isAIGeneratedImage(imageUrl) && !imagePreviewUrl && (
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  padding: "2px 8px",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: "0.75rem",
                }}
              >
                <AutoAwesome sx={{ fontSize: "0.875rem" }} />
                AI
              </Box>
            )}
            <IconButton
              color="secondary"
              onClick={() => {
                setImageUrl("");
                setNewImageFile(null);
                setImagePreviewUrl("");
              }}
              sx={{
                position: "absolute",
                bottom: 16,
                right: 8,
                border: `1px solid ${theme.palette.border.main}`,
                padding: ".25em",
                borderRadius: theme.shape.borderRadius,
                backgroundColor: theme.palette.background.actions,
              }}
            >
              <Delete />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
            <Button variant="outlined" component="label">
              upload image
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageChange}
              />
            </Button>
            <Button
              variant="outlined"
              onClick={handleGenerateAIImage}
              disabled={generatingAIImage}
              startIcon={<AutoAwesome />}
            >
              generate AI image
            </Button>
          </Box>
        )}
        {/* Regenerate AI image button when image exists */}
        {(imageUrl || imagePreviewUrl) && (
          <Button
            variant="text"
            size="small"
            onClick={handleGenerateAIImage}
            disabled={generatingAIImage}
            startIcon={<AutoAwesome />}
            sx={{ mt: 1 }}
          >
            {isAIGeneratedImage(imageUrl) ? "regenerate AI image" : "generate AI image"}
          </Button>
        )}
      </Box>

      {/* Ingredients Section */}
      <Typography
        variant="h6"
        gutterBottom
        sx={{ marginTop: 4, marginBottom: 2 }}
      >
        ingredients
      </Typography>

      {ingredients.map((ingredient, index) => {
        const error = ingredientErrors[index] || {};
        return (
          <Paper
            key={ingredient.id || index}
            sx={{
              padding: 2,
              marginBottom: 2,
              border: `1px solid ${theme.palette.border.main}`,
              opacity: ingredient.isDeleted ? 0.5 : 1,
              backgroundColor: ingredient.isDeleted
                ? theme.palette.background.actions
                : "inherit",
            }}
            elevation={0}
          >
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={6}>
                <TextField
                  label="name"
                  value={ingredient.name}
                  onChange={(e) =>
                    updateIngredient(index, "name", e.target.value)
                  }
                  fullWidth
                  disabled={ingredient.isDeleted}
                  onBlur={() => handleIngredientBlur(index)}
                  error={
                    (ingredient.touched || formSubmitted) && error && error.name
                  }
                  helperText={
                    (ingredient.touched || formSubmitted) && error && error.name
                  }
                  FormHelperTextProps={{
                    style: {
                      marginLeft: "4px",
                      fontSize: "0.75rem", // Reduce font size
                    },
                  }}
                  inputRef={(el) => (ingredientNameRefs.current[index] = el)}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  label="quantity"
                  value={ingredient.quantity?.value || ""}
                  onChange={(e) =>
                    updateIngredientQuantity(index, "value", e.target.value)
                  }
                  fullWidth
                  disabled={ingredient.isDeleted}
                  onBlur={() => handleIngredientBlur(index)}
                  error={
                    (ingredient.touched || formSubmitted) &&
                    error &&
                    error.quantity
                  }
                  helperText={
                    (ingredient.touched || formSubmitted) &&
                    error &&
                    error.quantity
                  }
                  FormHelperTextProps={{
                    style: {
                      marginLeft: "4px",
                      fontSize: "0.75rem", // Reduce font size
                    },
                  }}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  label="unit"
                  value={ingredient.quantity?.unit || ""}
                  onChange={(e) =>
                    updateIngredientQuantity(index, "unit", e.target.value)
                  }
                  fullWidth
                  disabled={ingredient.isDeleted}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="preparation"
                  value={ingredient.preparation || ""}
                  onChange={(e) =>
                    updateIngredient(index, "preparation", e.target.value)
                  }
                  fullWidth
                  multiline
                  disabled={ingredient.isDeleted}
                />
              </Grid>

              <Grid item xs={12} sx={{ textAlign: "right" }}>
                {ingredient.isDeleted ? (
                  <Button
                    variant="text"
                    onClick={() => restoreIngredient(index)}
                  >
                    Restore
                  </Button>
                ) : (
                  <IconButton
                    color="secondary"
                    onClick={() => deleteIngredient(index)}
                  >
                    <Delete />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Paper>
        );
      })}

      <Button variant="outlined" onClick={addIngredient} sx={{ marginTop: 1 }}>
        add ingredient
      </Button>

      {/* Instructions Section */}
      <Typography
        variant="h6"
        gutterBottom
        sx={{ marginTop: 4, marginBottom: 2 }}
      >
        instructions
      </Typography>

      {instructions.map((instruction, index) => {
        const error = instructionErrors[index] || {};
        return (
          <Paper
            key={index}
            sx={{
              padding: 2,
              marginBottom: 2,
              border: `1px solid ${theme.palette.border.main}`,
              backgroundColor: instruction.isDeleted
                ? theme.palette.background.actions
                : "inherit",
            }}
            elevation={0}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <TextField
                  label={`Step ${index + 1}`}
                  value={instruction.text}
                  onChange={(e) => updateInstruction(index, e.target.value)}
                  fullWidth
                  multiline
                  disabled={instruction.isDeleted}
                  onBlur={() => handleInstructionBlur(index)}
                  error={
                    (instruction.touched || formSubmitted) &&
                    error &&
                    error.text
                  }
                  helperText={
                    (instruction.touched || formSubmitted) &&
                    error &&
                    error.text
                  }
                  inputRef={(el) => (instructionRefs.current[index] = el)}
                />
              </Grid>
              <Grid item xs={12} sx={{ textAlign: "right" }}>
                {instruction.isDeleted ? (
                  <Button
                    variant="text"
                    onClick={() => restoreInstruction(index)}
                  >
                    restore
                  </Button>
                ) : (
                  <>
                    <IconButton
                      onClick={() => moveInstructionUp(index)}
                      disabled={index === 0}
                    >
                      <ArrowUpward />
                    </IconButton>
                    <IconButton
                      onClick={() => moveInstructionDown(index)}
                      disabled={index === instructions.length - 1}
                    >
                      <ArrowDownward />
                    </IconButton>
                    <IconButton
                      color="secondary"
                      onClick={() => deleteInstruction(index)}
                    >
                      <Delete />
                    </IconButton>
                  </>
                )}
              </Grid>
            </Grid>
          </Paper>
        );
      })}

      <Button variant="outlined" onClick={addInstruction} sx={{ marginTop: 1 }}>
        add instruction
      </Button>

      {/* Save and Cancel Buttons */}
      <Box sx={{ marginTop: 4, textAlign: "right" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading || !hasChanges}
        >
          save
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate(-1)}
          sx={{ marginLeft: 2 }}
        >
          cancel
        </Button>
      </Box>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="recipe saved"
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </Box>
  );
}

export default RecipeEditor;
